/**
 * We have now multiple places where manually put tags to invalidate.
 * It can be challenging to remember all of them so this places tries
 * to centralize and normalize via factories the generation of IDs.
 */
import { PaymentTypes } from '@appTypes/enums';
import type { GetCounselorClassesQueryParams } from '@appTypes/esb';

import { tagTypes } from '../constants';

export type TagType = {
  id: string | number;
  type: (typeof tagTypes)[keyof typeof tagTypes];
};

const tagFactory = {
  [tagTypes.UnitRenewalOrders]: {
    orgOrders: (organizationGuid: string) => ({
      type: tagTypes.UnitRenewalOrders,
      id: organizationGuid,
    }),
  },
  [tagTypes.UserActivitySummary]: {
    /** Tags the activities summary for a youth */
    userSummary: (userId: number) => ({
      type: tagTypes.UserActivitySummary,
      id: `${userId}`,
    }),
  },
  [tagTypes.SbEducationRecords]: {
    sbEducationRecords: (orgGuid: string) => ({
      type: tagTypes.SbEducationRecords,
      id: orgGuid,
    }),
  },
  [tagTypes.SbExtendedProfiles]: {
    SbExtendedProfiles: (orgGuid: string) => ({
      type: tagTypes.SbExtendedProfiles,
      id: orgGuid,
    }),
  },
  [tagTypes.Activities]: {
    /** Tag a list of activites for a whole unit. */
    orgActivities: (organizationGuid: string) => ({
      type: tagTypes.Activities,
      id: `Org-${organizationGuid}`,
    }),
    /** For an activity returned inside the list. Useful if you want to invalidate but you don't know the personGuid or orgGuid */
    activityItem: (activityId: number) => ({
      type: tagTypes.Activities,
      id: `Activity-${activityId}`,
    }),
    /** Track eagle activity of a given youth using member id. If user id is provided it can be used instead of activity id */
    eagleActivity: (userId?: number, activityId?: number) => [
      {
        type: tagTypes.Activities,
        id: `EagleActivity-${userId}`,
      },
      {
        type: tagTypes.Activities,
        id: `Youth-${userId}`,
      },
      {
        type: tagTypes.Activities,
        id: `Activity-${activityId}`,
      },
    ],
    /** Tag a list of activities associated to a single scout */
    youthActivities: (userId: string | number) => ({
      type: tagTypes.Activities,
      id: `Youth-${userId}`,
    }),
  },
  /** List of events for a given unit */
  [tagTypes.UnitEvents]: {
    unitEvents: (unitId: number) => ({
      type: tagTypes.UnitEvents,
      id: unitId,
    }),
  },
  [tagTypes.OrganizationRoster]: {
    /** useful to reset all rosters for an organization */
    organizationRosters: (organizationGuid: string) => ({
      type: tagTypes.OrganizationRoster,
      id: organizationGuid,
    }),
    /** useful to reset only the youth roster */
    youthRoster: (organizationGuid: string) => ({
      type: tagTypes.OrganizationRoster,
      id: `OrgYouths-${organizationGuid}`,
    }),
    /** useful to reset only the youth roster */
    adultRoster: (organizationGuid: string) => ({
      type: tagTypes.OrganizationRoster,
      id: `OrgAdults-${organizationGuid}`,
    }),
    /** useful to reset roster based on a person update */
    youthInRoster: (personGuid: string) => ({
      type: tagTypes.OrganizationRoster,
      id: `OrgYouths-Youth-${personGuid}`,
    }),
    /** useful to reset roster based on any person either youth,adult,etc. */
    personInRoster: (personGuid: string) => ({
      type: tagTypes.OrganizationRoster,
      id: `RosterPerson-${personGuid}`,
    }),
  },
  [tagTypes.UnitRoster]: {
    organizationRosters: (organizationGuid: string) => ({
      type: tagTypes.UnitRoster,
      id: organizationGuid,
    }),
    /** useful to reset only the youth roster */
    youthRoster: (organizationGuid: string) => ({
      type: tagTypes.UnitRoster,
      id: `Youths-${organizationGuid}`,
    }),
    /** useful to reset only the adult roster */
    adultRoster: (organizationGuid: string) => ({
      type: tagTypes.UnitRoster,
      id: `Adults-${organizationGuid}`,
    }),
    /** useful to reset only the parent roster */
    parentRoster: (organizationGuid: string) => ({
      type: tagTypes.UnitRoster,
      id: `Parents-${organizationGuid}`,
    }),
  },
  /** List of organization positions (Registered and Functional) for a given unit */
  [tagTypes.OrganizationPositions]: {
    organizationPositions: (organizationGuid: string) => ({
      type: tagTypes.OrganizationPositions,
      id: organizationGuid,
    }),
  },
  [tagTypes.SubUnits]: {
    subUnits: (orgGuid: string) => ({
      type: tagTypes.SubUnits,
      id: `SubUnits-${orgGuid}`,
    }),
    adults: (orgGuid: string) => ({
      type: tagTypes.SubUnits,
      id: `Adults-${orgGuid}`,
    }),
    youths: (orgGuid: string) => ({
      type: tagTypes.SubUnits,
      id: `Youths-${orgGuid}`,
    }),
    forOrg: (orgGuid: string) => ({
      type: tagTypes.SubUnits,
      id: `Org-${orgGuid}`,
    }),
  },
  [tagTypes.YouthAwardRequirements]: {
    youthRequirements: (userId: number, awardId: number) => ({
      type: tagTypes.YouthAwardRequirements,
      id: `${userId}:${awardId}`,
    }),
  },
  [tagTypes.YouthRankRequirements]: {
    youthRankRequirements: (userId: number, rankId: number) => ({
      type: tagTypes.YouthRankRequirements,
      id: `${userId}-${rankId}`,
    }),
  },
  [tagTypes.RankRequirements]: {
    /** The catalog requirements are per rank *and* per version, not per youth */
    rankRequirements: (
      rankId: number | string,
      versionId?: number | string,
    ) => ({
      type: tagTypes.RankRequirements,
      id: `${rankId}-${versionId}`,
    }),
  },
  [tagTypes.YouthAward]: {
    youthAwardsList: (userId: number | string) => ({
      type: tagTypes.YouthAward,
      id: userId,
    }),
  },
  [tagTypes.YouthRanks]: {
    youthRanksList: (userId: number | string) => ({
      type: tagTypes.YouthRanks,
      id: userId,
    }),
  },
  [tagTypes.YouthAdventure]: {
    youthAdventuresList: (userId: number | string) => ({
      type: tagTypes.YouthAdventure,
      id: userId,
    }),
  },
  [tagTypes.AdvancementComment]: {
    forAdvancement: (
      youthUserId: number | string,
      advancementId?: number | string | undefined,
    ) => ({
      type: tagTypes.AdvancementComment,
      id: `Youth:${youthUserId}-Adv:${advancementId}`,
    }),
    forAdvancementRequirement: (
      youthUserId: number | string,
      advancementId: number | string | undefined,
      requirementId: number | string | undefined,
    ) => ({
      type: tagTypes.AdvancementComment,
      id: `Youth:${youthUserId}-Adv:${advancementId}-Req:${requirementId}`,
    }),
  },
  [tagTypes.AwardUserRequirements]: {
    /**
     * Contains a list of youth and every requirement status
     * This is used in bulk entry */
    orgRequirements: (organizationGuid: string, awardId: number) => ({
      type: tagTypes.AwardUserRequirements,
      id: `${organizationGuid}-${awardId}`,
    }),
  },
  [tagTypes.MeritBadgeUserRequirements]: {
    /** Used in bulk entry */
    byMeritBadge: (
      organizationGuid: string,
      meritBadgeId: number | string,
    ) => ({
      type: tagTypes.MeritBadgeUserRequirements,
      id: `org:${organizationGuid}:mb:${meritBadgeId}`,
    }),
  },
  [tagTypes.OrganizationAdvDashboard]: {
    /**  invalidate the counts to approve tab */
    unitDashboard: (organizationGuid: string) => ({
      type: tagTypes.OrganizationAdvDashboard,
      id: organizationGuid,
    }),
  },
  [tagTypes.AdvancementHistory]: {
    unitHistoryByType: (organizationGuid: string, advancementType: string) => ({
      type: tagTypes.AdvancementHistory,
      id: `Unit-${organizationGuid}-AdvType-${advancementType}`,
    }),
    /** refresh to approve list */
    unitHistory: (organizationGuid: string) => ({
      type: tagTypes.AdvancementHistory,
      id: `Unit-List-${organizationGuid}`,
    }),
    unitHistoryWithStatus: (organizationGuid: string, statuses: string) => ({
      type: tagTypes.AdvancementHistory,
      id: `${organizationGuid}-${statuses}`,
    }),
    toApproveTabRequirements: (organizationGuid: string) => ({
      type: tagTypes.AdvancementHistory,
      id: `${organizationGuid}-RequirementsList`,
    }),
  },
  [tagTypes.PurchaseOrders]: {
    purchaseOrderDetails: (purchaseOrderId: number) => ({
      type: tagTypes.AdvancementHistory,
      id: `${purchaseOrderId}`,
    }),
    purchaseOrderList: (organizationGuid: string) => ({
      type: tagTypes.AdvancementHistory,
      id: `${organizationGuid}`,
    }),
  },
  [tagTypes.AdventureRequirements]: {
    requirements: (adventureId: number) => ({
      type: tagTypes.AdventureRequirements,
      id: adventureId,
    }),
  },
  [tagTypes.YouthAdventureRequirements]: {
    requirements: (userId: number, adventureId: number) => ({
      type: tagTypes.YouthAdventureRequirements,
      id: `${userId}-${adventureId}`,
    }),
  },
  [tagTypes.YouthMeritBadge]: {
    /** List of MBs for the youth */
    youthMBList: (userId: string | number) => ({
      type: tagTypes.YouthMeritBadge,
      id: userId,
    }),
    youthMBDetails: (
      userId: string | number,
      meritBadgeId: number | string,
    ) => ({
      type: tagTypes.YouthMeritBadge,
      id: `${userId}-${meritBadgeId}`,
    }),
  },
  [tagTypes.MBCAssignedToYouth]: {
    /** youthMemberId can be memberId or personGuid */
    youthMBC: (youthMemberId: string | number, meritBadgeId: number) => ({
      type: tagTypes.MBCAssignedToYouth,
      id: `${youthMemberId}-${meritBadgeId}`,
    }),
  },
  [tagTypes.MBCClasses]: {
    mbcClasses: (
      params?: Pick<GetCounselorClassesQueryParams, 'counselorMemberId'>,
    ) => {
      const tags: Array<{ type: typeof tagTypes.MBCClasses; id: string }> = [];

      if (!params) return tags;

      if (params.counselorMemberId) {
        tags.push({
          type: tagTypes.MBCClasses,
          id: `Counselor-${params.counselorMemberId}`,
        });
      }

      return tags;
    },
  },
  [tagTypes.MeritBadgeCounselorProfile]: {
    mbcProfile: (memberId: number) => ({
      type: tagTypes.MeritBadgeCounselorProfile,
      id: memberId,
    }),
  },
  [tagTypes.MBCDashboard]: {
    userDashboard: (personGuidOrMID: string | number) => ({
      id: personGuidOrMID,
      type: tagTypes.MBCDashboard,
    }),
  },
  [tagTypes.PersonProfile]: {
    byUserId: (userId: number) => ({
      id: userId,
      type: tagTypes.PersonProfile,
    }),
  },
  [tagTypes.CompletePersonProfile]: {
    personProfile: (personGuid: string) => ({
      id: personGuid,
      type: tagTypes.CompletePersonProfile,
    }),
    personProfileUserId: (userId: number) => ({
      id: `uid:${userId}`,
      type: tagTypes.CompletePersonProfile,
    }),
    personProfileMID: (memberId: number) => ({
      id: `mid:${memberId}`,
      type: tagTypes.CompletePersonProfile,
    }),
  },
  [tagTypes.MerchantStatuses]: {
    organizationStatus: (orgGuid: string) => ({
      type: tagTypes.MerchantStatuses,
      id: orgGuid,
    }),
  },
  [tagTypes.PaymentLogQuery]: {
    userPaymentLog: (userId: string) => ({
      type: tagTypes.PaymentLogQuery,
      id: userId,
    }),
    orgPaymentLogs: (orgGuid: string, paymentType: PaymentTypes) => ({
      type: tagTypes.PaymentLogQuery,
      id: `${orgGuid}-${paymentType}`,
    }),
  },
  [tagTypes.PaymentLogSummary]: {
    orgPaymentLogsSummary: (organizationGuid: string) => ({
      type: tagTypes.PaymentLogSummary,
      id: organizationGuid,
    }),
  },
  [tagTypes.PaymentLogBalance]: {
    orgPaymentLogsBalance: (organizationGuid: string) => ({
      type: tagTypes.PaymentLogBalance,
      id: organizationGuid,
    }),
  },
  [tagTypes.PaymentLogCategories]: {
    orgPaymentLogsCategories: (organizationGuid: string) => ({
      type: tagTypes.PaymentLogCategories,
      id: organizationGuid,
    }),
  },
  [tagTypes.PaymentLogComments]: {
    forUnit: (unitId: number | string) => ({
      type: tagTypes.PaymentLogComments,
      id: `Unit-${unitId}`,
    }),
  },
  [tagTypes.EventsCommunications]: {
    organizationGuid: (organizationGuid: string) => ({
      type: tagTypes.EventsCommunications,
      id: organizationGuid,
    }),
    event: (eventGuid: string) => ({
      type: tagTypes.EventsCommunications,
      id: `Event-${eventGuid}`,
    }),
  },
  [tagTypes.UnitRosterLiteReport]: {
    /** The SBLUnitRosterLiteReport for a given organization */
    unitRoster: (organizationGuid: string) => ({
      type: tagTypes.UnitRosterLiteReport,
      id: organizationGuid,
    }),
  },
  [tagTypes.ImportMBFile]: {
    organizationGuid: (organizationGuid: string) => ({
      type: tagTypes.ImportMBFile,
      id: organizationGuid,
    }),
    /** Validation error rows; they only change when a file is uploaded or deleted */
    requirements: (organizationGuid: string) => ({
      type: tagTypes.ImportMBFile,
      id: `${organizationGuid}-requirements`,
    }),
  },
  [tagTypes.AdvancementImports]: {
    /** Imports list for a unit; scoped by organizationGuid. */
    orgImports: (organizationGuid: string) => ({
      type: tagTypes.AdvancementImports,
      id: organizationGuid,
    }),
  },
  [tagTypes.UnitSearchResult]: {
    organizationGuid: (organizationGuid: string) => ({
      type: tagTypes.UnitSearchResult,
      id: organizationGuid,
    }),
  },
  [tagTypes.UnitPendingLeadershipPositions]: {
    /** A unit's pending leadership positions */
    forOrg: (orgGuid: string) => ({
      type: tagTypes.UnitPendingLeadershipPositions,
      id: orgGuid,
    }),
  },
  [tagTypes.YouthLeadershipPositions]: {
    /** A youth's leadership position history */
    forUser: (userId: number) => ({
      type: tagTypes.YouthLeadershipPositions,
      id: userId,
    }),
  },
};

export default tagFactory;
